import { Video, Mic, Type, ArrowLeftRight } from "lucide-react";
import { StudioPageLayout } from "@/components/flowly/StudioPageLayout/StudioPageLayout";
import { GenerationBar } from "@/components/flowly/GenerationBar/GenerationBar";
import { CreationsGallery } from "@/components/flowly/CreationsGallery/CreationsGallery";
import { MediaGridItem } from "@/components/flowly/MediaGridItem/MediaGridItem";
import { GalleryItemBadges } from "@/components/flowly/GalleryItemBadges/GalleryItemBadges";
import { VideoLightbox } from "@/components/flowly/Lightbox/VideoLightbox/VideoLightbox";
import { TalkingAvatarUpload } from "@/components/flowly/TalkingAvatarUpload/TalkingAvatarUpload";
import { TalkingAvatarUploadGuide } from "@/components/flowly/TalkingAvatarUploadGuide/TalkingAvatarUploadGuide";
import { TalkingAvatarImageModePicker } from "@/components/flowly/TalkingAvatarImageModePicker/TalkingAvatarImageModePicker";
import { TalkingAvatarStylePicker } from "@/components/flowly/TalkingAvatarStylePicker/TalkingAvatarStylePicker";
import { TalkingAvatarBackgroundInput } from "@/components/flowly/TalkingAvatarBackgroundInput/TalkingAvatarBackgroundInput";
import { TalkingAvatarPreviewGrid } from "@/components/flowly/TalkingAvatarPreviewGrid/TalkingAvatarPreviewGrid";
import { TalkingAvatarPreviewHistory } from "@/components/flowly/TalkingAvatarPreviewHistory/TalkingAvatarPreviewHistory";
import { TalkingAvatarDialogueInput } from "@/components/flowly/TalkingAvatarDialogueInput/TalkingAvatarDialogueInput";
import { TalkingAvatarVoicePicker } from "@/components/flowly/TalkingAvatarVoicePicker/TalkingAvatarVoicePicker";
import { TalkingAvatarCloneModal } from "@/components/flowly/TalkingAvatarCloneModal/TalkingAvatarCloneModal";
import { TalkingAvatarUserAudioModal } from "@/components/flowly/TalkingAvatarUserAudioModal/TalkingAvatarUserAudioModal";
import { TalkingAvatarWordLimitModal } from "@/components/flowly/TalkingAvatarWordLimitModal/TalkingAvatarWordLimitModal";
import { TalkingAvatarHeroShowcase } from "@/components/flowly/TalkingAvatarHeroShowcase/TalkingAvatarHeroShowcase";
import { FlowReadyCta } from "@/components/flowly/FlowReadyCta/FlowReadyCta";
import { useAuth } from "@/contexts/AuthContext";
import { downloadMedia } from "@/lib/mediaUtils";
import {
  TALKING_AVATAR_BACKGROUND_ANCHOR_ID,
  TALKING_AVATAR_DIALOGUE_ANCHOR_ID,
  TALKING_AVATAR_HERO_AFTER,
  TALKING_AVATAR_HERO_BEFORE,
  TALKING_AVATAR_IMAGE_MODE_ANCHOR_ID,
  TALKING_AVATAR_PREVIEW_ANCHOR_ID,
  TALKING_AVATAR_STYLE_ANCHOR_ID,
  TALKING_AVATAR_VOICE_ANCHOR_ID,
} from "./constants";
import { useTalkingAvatar } from "./useTalkingAvatar";

export default function TalkingAvatarPage() {
  const { user } = useAuth();
  const {
    upload,
    isUploading,
    imageMode,
    showUploadGuide,
    closeUploadGuide,
    style,
    background,
    composition,
    previewUrls,
    selectedPreviewIndex,
    selectedHistoricalUrl,
    pastPreviewImages,
    isPreviewing,
    previewStartedAt,
    dialogue,
    motion,
    voices,
    voicesLoading,
    selectedVoiceId,
    isRendering,
    estimatedCredits,
    wordCount,
    maxWords,
    minWords,
    wordLimitModalOpen,
    closeWordLimitModal,
    previewCost,
    galleryRef,
    galleryVideos,
    pendingVideoCount,
    dbPendingVideos,
    videoCompletingIds,
    selectedMedia,
    setSelectedMedia,
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,
    handleAddFile,
    handleImageModeChange,
    handleStyleChange,
    handleBackgroundChange,
    handleCompositionChange,
    handleGeneratePreview,
    handlePreviewSelect,
    handleHistoricalPreviewSelect,
    handleDialogueChange,
    handleMotionChange,
    handleVoiceSelect,
    handleGenerate,
    handleDownload,
    handleShare,
    handleCloseLightbox,
    handleRegenerate,
    isCloneModalOpen,
    cloneModalAction,
    handleOpenCloneModal,
    handleCloseCloneModal,
    handleCloneSubmit,
    handleDeleteClonedVoice,
    userAudio,
    isUserAudioModalOpen,
    handleOpenUserAudioModal,
    handleCloseUserAudioModal,
    handleUserAudioSubmit,
    handleClearUserAudio,
    dialogueMode,
    handleSelectTextMode,
    handleSwitchDialogueMode,
    canGenerate,
    t,
  } = useTalkingAvatar();

  // Step visibility (each step appears once the previous is committed).
  // Step 2 picks the image mode: "source" jumps straight to the dialogue
  // step (the uploaded photo gets lip-synced as-is); "generate" opens the
  // style → background → preview path. (subjectType is always "person" — the
  // flow only targets characters, so there's no person/object step.)
  const showImageMode = !!upload;
  const isSourceMode = imageMode === "source";
  const showStyle = !!upload && imageMode === "generate";
  const showBackground = showStyle && !!style;
  const showPreview = showBackground && (previewUrls.length > 0 || isPreviewing);
  // The dialogue card holds the two-path picker AND whichever input the user
  // chose — so it appears as soon as a preview is picked (or immediately in
  // source mode, where there's no preview to pick).
  const showDialogue =
    (!!upload && isSourceMode) ||
    selectedPreviewIndex !== null ||
    selectedHistoricalUrl !== null;
  // Voice picker only appears in the TEXT path — and only once the dialogue
  // meets the word floor (below it Kling rejects the audio anyway, so there's
  // no point surfacing the next step). BYO-audio path skips it entirely.
  // wordCount comes from the hook (single source of truth shared with
  // canGenerate + the credit estimate) so the gate can't drift from them.
  const showVoice =
    showDialogue && dialogueMode === "text" && wordCount >= minWords;
  // Step numbers shift with the chosen path: source mode skips the three
  // image-generation steps (style/background/preview), so dialogue/voice
  // renumber from 3; generate mode runs 3–5 then dialogue=6, voice=7.
  const dialogueStepNumber = isSourceMode ? 3 : 6;
  const voiceStepNumber = isSourceMode ? 4 : 7;
  const stepPrefix = (n: number) =>
    t.talkingAvatar.stepPrefix.replace("{n}", String(n));

  return (
    <StudioPageLayout
      activeTab="videos"
      activeApp="talkingAvatar"
      footer={
        !selectedMedia ? (
          <GenerationBar
            onGenerate={handleGenerate}
            generating={isRendering}
            disabled={!canGenerate}
            buttonLabel={
              estimatedCredits > 0
                ? `${t.talkingAvatar.generateBtn} ~${estimatedCredits}`
                : t.talkingAvatar.generateBtn
            }
            mediaType="video"
            // Estimated cost — recomputed live from word-count × tier rate.
            // The "~" prefix in the button label signals it's an estimate;
            // the server charges the exact amount using the same formula.
            creditCost={estimatedCredits}
            // Talking Avatar always inherits dimensions from the input image,
            // so we display a static aspect-ratio chip without a picker.
            aspectRatio="3:4"
            onAspectRatioChange={() => undefined}
            availableAspectRatios={[]}
          />
        ) : undefined
      }
      lightbox={
        selectedMedia ? (
          <VideoLightbox
            videoUrl={selectedMedia.url}
            sourceImageUrl={selectedMedia.referenceImageUrls[0]}
            prompt={selectedMedia.prompt}
            createdAt={selectedMedia.createdAt}
            model={selectedMedia.model}
            resolution={selectedMedia.resolution}
            aspectRatio={selectedMedia.aspectRatio}
            duration={selectedMedia.duration}
            pipeline={selectedMedia.pipeline}
            onClose={handleCloseLightbox}
            onRegenerate={handleRegenerate}
            onDownload={handleDownload}
            onShare={handleShare}
            navigation={
              galleryVideos.length > 1
                ? {
                    currentIndex: currentMediaIndex,
                    totalItems: galleryVideos.length,
                    canGoNext: canNavigate,
                    canGoPrev: canNavigate,
                    onNext: goToNextItem,
                    onPrev: goToPrevItem,
                  }
                : undefined
            }
          />
        ) : undefined
      }
    >
      <div className="space-y-6 md:space-y-8 mt-2 md:mt-4">
        {/* ── HERO ── Step 1: Upload */}
        <div className="rounded-3xl border border-border bg-gradient-to-b from-primary/[0.05] to-transparent p-5 md:p-8">
          <div className="text-center mb-6 md:mb-8">
            <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-2 md:mb-3 bg-gradient-to-r from-primary to-foreground bg-clip-text text-transparent">
              {t.talkingAvatar.heroTitle}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto">
              {t.talkingAvatar.heroSubtitle}
            </p>
          </div>

          {!upload ? (
            // Initial state: side-by-side showcase + dropzone. Once the user
            // uploads, the showcase disappears so the screen focuses on their
            // own photo (mirrors the Product Studio hero behaviour).
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8 items-center">
              <TalkingAvatarHeroShowcase
                beforeImage={TALKING_AVATAR_HERO_BEFORE}
                afterVideo={TALKING_AVATAR_HERO_AFTER}
              />
              <TalkingAvatarUpload
                upload={upload}
                isUploading={isUploading}
                onAddFile={handleAddFile}
                variant="hero"
              />
            </div>
          ) : (
            <TalkingAvatarUpload
              upload={upload}
              isUploading={isUploading}
              onAddFile={handleAddFile}
              variant="compact"
            />
          )}
        </div>

        {/* Step 2: Image mode — use the uploaded photo as-is, or design a new one */}
        {showImageMode && (
          <div
            id={TALKING_AVATAR_IMAGE_MODE_ANCHOR_ID}
            className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            <TalkingAvatarImageModePicker
              value={imageMode}
              uploadPreviewUrl={upload!.previewUrl}
              onChange={handleImageModeChange}
            />
          </div>
        )}

        {/* Step 3 (generate path): Style */}
        {showStyle && (
          <div
            id={TALKING_AVATAR_STYLE_ANCHOR_ID}
            className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            <TalkingAvatarStylePicker stepNumber={3} value={style} onChange={handleStyleChange} />
          </div>
        )}

        {/* Step 4 (generate path): Background description + manual "Create characters" trigger */}
        {showBackground && (
          <div
            id={TALKING_AVATAR_BACKGROUND_ANCHOR_ID}
            className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            <TalkingAvatarBackgroundInput
              stepNumber={4}
              background={background}
              composition={composition}
              isGenerating={isPreviewing}
              hasPreview={previewUrls.length > 0}
              previewCost={previewCost}
              onBackgroundChange={handleBackgroundChange}
              onCompositionChange={handleCompositionChange}
              onGenerate={handleGeneratePreview}
            />
          </div>
        )}

        {/* Step 5 (generate path): Preview pair */}
        {showPreview && (
          <div
            id={TALKING_AVATAR_PREVIEW_ANCHOR_ID}
            className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            <TalkingAvatarPreviewGrid
              stepNumber={5}
              imageUrls={previewUrls}
              selectedIndex={selectedPreviewIndex}
              isGenerating={isPreviewing}
              generationStartedAt={previewStartedAt}
              onSelect={handlePreviewSelect}
            />

            {/* Past-previews picker — surfaces every prior talking_avatar
                preview the user has generated so they can re-render an older
                one. Hidden when there's no history yet (returns null). */}
            <div className="mt-4 md:mt-5">
              <TalkingAvatarPreviewHistory
                images={pastPreviewImages}
                excludeUrls={previewUrls}
                selectedUrl={selectedHistoricalUrl}
                onSelect={handleHistoricalPreviewSelect}
              />
            </div>
          </div>
        )}

        {/* Dialogue step: pick a path (text → voice step, or BYO audio → done) */}
        {showDialogue && (
          <div
            id={TALKING_AVATAR_DIALOGUE_ANCHOR_ID}
            className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            {dialogueMode === "select" && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-foreground">
                    {stepPrefix(dialogueStepNumber)}
                    {t.talkingAvatar.dialogueModeChooseTitle}
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
                    {t.talkingAvatar.dialogueModeChooseHint}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={handleSelectTextMode}
                    className="flex flex-col items-start gap-1 p-4 rounded-xl bg-card border border-border hover:border-primary/50 hover:shadow-sm transition-all text-start"
                  >
                    <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-1">
                      <Type className="w-5 h-5" />
                    </div>
                    <p className="text-sm font-semibold text-foreground">
                      {t.talkingAvatar.dialogueModeText}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {t.talkingAvatar.dialogueModeTextHint}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenUserAudioModal}
                    className="flex flex-col items-start gap-1 p-4 rounded-xl bg-card border border-border hover:border-primary/50 hover:shadow-sm transition-all text-start"
                  >
                    <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-1">
                      <Mic className="w-5 h-5" />
                    </div>
                    <p className="text-sm font-semibold text-foreground">
                      {t.talkingAvatar.dialogueModeAudio}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {t.talkingAvatar.dialogueModeAudioHint}
                    </p>
                  </button>
                </div>
              </div>
            )}

            {dialogueMode === "text" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11px] uppercase tracking-wide text-primary font-semibold">
                    {t.talkingAvatar.dialogueModeActiveTextLabel}
                  </span>
                  <button
                    type="button"
                    onClick={handleSwitchDialogueMode}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeftRight className="w-3.5 h-3.5" />
                    {t.talkingAvatar.dialogueModeSwitch}
                  </button>
                </div>
                <TalkingAvatarDialogueInput
                  stepNumber={dialogueStepNumber}
                  dialogue={dialogue}
                  motion={motion}
                  maxWords={maxWords}
                  minWords={minWords}
                  onDialogueChange={handleDialogueChange}
                  onMotionChange={handleMotionChange}
                />
              </div>
            )}

            {dialogueMode === "audio" && userAudio && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11px] uppercase tracking-wide text-primary font-semibold">
                    {t.talkingAvatar.dialogueModeActiveAudioLabel}
                  </span>
                  <button
                    type="button"
                    onClick={handleSwitchDialogueMode}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeftRight className="w-3.5 h-3.5" />
                    {t.talkingAvatar.dialogueModeSwitch}
                  </button>
                </div>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {t.talkingAvatar.userAudioActiveLabel}
                </p>
                <div className="rounded-xl border border-primary/30 bg-primary/[0.04] p-3 flex items-center justify-between gap-3">
                  <span className="text-sm text-foreground">
                    {t.talkingAvatar.userAudioDurationLabel.replace(
                      "{seconds}",
                      userAudio.durationSec.toFixed(1),
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={handleClearUserAudio}
                    className="text-xs text-destructive hover:underline"
                  >
                    {t.talkingAvatar.userAudioActiveClear}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Voice step (text path only) */}
        {showVoice && (
          <div
            id={TALKING_AVATAR_VOICE_ANCHOR_ID}
            className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            <TalkingAvatarVoicePicker
              stepNumber={voiceStepNumber}
              voices={voices}
              isLoading={voicesLoading}
              selectedVoiceId={selectedVoiceId}
              onSelectVoice={handleVoiceSelect}
              isAuthenticated={!!user}
              onOpenCloneModal={handleOpenCloneModal}
              onDeleteClonedVoice={handleDeleteClonedVoice}
            />
          </div>
        )}

        {/* Inline "all set" CTA — same generate action as the footer bar.
            Mounted through the render too (canGenerate flips false while
            isRendering) so it doesn't unmount-remount mid-generation. */}
        {(canGenerate || isRendering) && (
          <FlowReadyCta
            onGenerate={handleGenerate}
            generating={isRendering}
            disabled={!canGenerate}
            creditCost={estimatedCredits}
            buttonLabel={
              estimatedCredits > 0
                ? `${t.talkingAvatar.generateBtn} ~${estimatedCredits}`
                : t.talkingAvatar.generateBtn
            }
          />
        )}

      </div>

      {/* One-time "how to shoot it" guide — pops after the first upload,
          same pattern as ProfilePhotoUploadGuide. */}
      <TalkingAvatarUploadGuide isOpen={showUploadGuide} onClose={closeUploadGuide} />

      {/* Voice clone modal (Starter+ — free users are routed to the pricing
          upsell by the hook before the modal would mount). */}
      <TalkingAvatarCloneModal
        open={isCloneModalOpen}
        onOpenChange={(open) => (open ? handleOpenCloneModal() : handleCloseCloneModal())}
        onSubmit={handleCloneSubmit}
        initialAction={cloneModalAction}
      />

      {/* BYO-audio modal — Starter+ gate is enforced in handleOpenUserAudioModal,
          so by the time the modal mounts the user is already authorized. */}
      <TalkingAvatarUserAudioModal
        open={isUserAudioModalOpen}
        onOpenChange={(open) => (open ? handleOpenUserAudioModal() : handleCloseUserAudioModal())}
        onSubmit={handleUserAudioSubmit}
      />

      {/* Hit-the-cap modal — fires when the user tries to type past maxWords.
          Pro plan upgrades to 90; Free shows 60. If the user is already on
          Pro/Creator, the modal explains the hard cap without an upsell. */}
      <TalkingAvatarWordLimitModal
        open={wordLimitModalOpen}
        onClose={closeWordLimitModal}
        currentLimit={maxWords}
        upgradeLimit={maxWords < 90 ? 90 : undefined}
      />

      {/* Video Gallery — mirrors FramesToVideo / TextToVideo. Pending placeholders
          are driven by usePersistentPendingBoost so they survive refresh, and
          completed videos stream in via Supabase realtime. */}
      <CreationsGallery
        title={t.talkingAvatar.results}
        galleryRef={galleryRef}
        pendingCount={pendingVideoCount}
        pendingLabel={t.generate.creatingVideo}
        pendingItems={dbPendingVideos.map((v) => ({ id: v.id, createdAt: v.created_at }))}
        pendingMediaType="video"
        completingIds={videoCompletingIds}
        className="mt-10 mb-10"
        emptyIcon={Video}
        emptyTitle={t.generate.noVideosYet}
        emptySubtitle={t.generate.startCreatingVideos}
      >
        {galleryVideos.map((video) => (
          <MediaGridItem
            key={video.id}
            imageUrl={video.displayUrl || undefined}
            videoUrl={video.url || undefined}
            isVideo
            alt={video.prompt}
            onClick={() => setSelectedMedia(video)}
            onDownload={
              video.url ? () => downloadMedia(video.url!, "talking-avatar", "mp4") : undefined
            }
            downloadLabel={t.lightbox.download}
          >
            <GalleryItemBadges item={video} />
          </MediaGridItem>
        ))}
      </CreationsGallery>
    </StudioPageLayout>
  );
}
